import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Col, FormGroup, Input, Row, Label, Button } from 'reactstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import Loading from '../../components/Loading/Loading';
import paginationFactory from 'react-bootstrap-table2-paginator';
import moment from 'moment';
import axios from 'axios';
import { debounce } from 'lodash';
import { CONNECTION_TYPE_OPTIONS, SETTLEMENT_CODE_OPTIONS } from './constants';

const DefaultedConsumptionDataManagement = () => {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  // Pagination state
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [totalSize, setTotalSize] = useState(0);
  const [sortBy, setSortBy] = useState('created');
  const [sortDir, setSortDir] = useState('desc');

  // Filter state
  const [filters, setFilters] = useState({
    meterPoint: '',
    connectionType: '',
    settlementCode: '',
  });

  // Debounced filter state for MPAN
  const [mpanInput, setMpanInput] = useState('');

  // Common header style for all columns
  const commonHeaderStyle = {
    whiteSpace: 'normal',
    lineHeight: '1.2',
  };

  // Common date formatter
  const formatDate = (cell) => (cell ? moment(cell).format('DD/MM/YYYY HH:mm:ss') : '');

  // Common date sort value
  const getDateSortValue = (cell) => (cell ? moment(cell).valueOf() : 0);

  // Common option formatter factory
  const createOptionFormatter = (options) => (cell) => {
    const option = options.find((opt) => opt.value === cell);
    return option ? option.label : cell;
  };

  // Debounced MPAN filter function
  const debouncedMpanFilter = useCallback(
    debounce((value) => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        meterPoint: value,
      }));
      setPage(1);
    }, 500),
    []
  );

  // Handle MPAN input change
  const handleMpanInputChange = (e) => {
    const value = e.target.value;
    setMpanInput(value);
    debouncedMpanFilter(value);
  };

  const columns = useMemo(
    () => [
      {
        dataField: 'meterPoint.identifier',
        text: 'MPAN',
        sort: true,
        headerStyle: commonHeaderStyle,
      },
      {
        dataField: 'connectionType',
        text: 'Connection Type',
        headerStyle: commonHeaderStyle,
        sort: true,
        formatter: createOptionFormatter(CONNECTION_TYPE_OPTIONS),
      },
      {
        dataField: 'settlementRunExecutionDate',
        text: 'Settlement Run Execution Date',
        headerStyle: commonHeaderStyle,
        formatter: formatDate,
        sort: true,
        sortValue: getDateSortValue,
      },
      {
        dataField: 'settlementRun',
        text: 'Settlement Run',
        headerStyle: commonHeaderStyle,
        sort: true,
        formatter: createOptionFormatter(SETTLEMENT_CODE_OPTIONS),
      },
      {
        dataField: 'runNumber',
        text: 'Run Number',
        headerStyle: commonHeaderStyle,
        sort: true,
      },
      {
        dataField: 'versionDate',
        text: 'Version Date',
        headerStyle: commonHeaderStyle,
        formatter: formatDate,
        sort: true,
        sortValue: getDateSortValue,
      },
      {
        dataField: 'content',
        text: 'Content',
        headerStyle: commonHeaderStyle,
        formatter: (cell) => {
          try {
            return (
              <pre
                style={{
                  whiteSpace: 'pre',
                  overflowX: 'auto',
                  maxHeight: '200px',
                  textAlign: 'left',
                  fontSize: '12px',
                  margin: 0,
                }}
              >
                {JSON.stringify(cell, null, 2)}
              </pre>
            );
          } catch (e) {
            return cell;
          }
        },
      },
    ],
    [CONNECTION_TYPE_OPTIONS, SETTLEMENT_CODE_OPTIONS]
  );

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);

    try {
      const token = localStorage.token;

      // Build query parameters
      const params = {
        page: page - 1,
        size: size,
        sortBy: sortBy,
        sortDir: sortDir,
      };

      // Add filters only if they have values
      Object.keys(filters).forEach((key) => {
        if (filters[key] && filters[key].trim() !== '') {
          params[key] = filters[key];
        }
      });

      const response = await axios.get('/df/dataflows/rest/v1/defaulted-consumption/data', {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setTableData(response.data.content || []);
      setTotalSize(response.data.totalElements || 0);
    } catch (error) {
      setTableData([]);
      setTotalSize(0);
    }
    setLoading(false);
  };

  // Fetch data when page/size/sort/filters change
  useEffect(() => {
    fetchData();
  }, [page, size, sortBy, sortDir, filters]);

  // Table change handler
  const handleTableChange = (type, { page, sizePerPage, sortField, sortOrder }) => {
    if (type === 'sort') {
      setSortBy(sortField);
      setSortDir(sortOrder);
    }
    if (type === 'pagination') {
      setPage(page);
      setSize(sizePerPage);
    }
  };

  // Filter change handler
  const handleFilterChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value,
    }));
    setPage(1); // Reset to first page on filter change
  };

  // Common filter clear handler
  const clearAllFilters = () => {
    setFilters({
      meterPoint: '',
      connectionType: '',
      settlementCode: '',
    });
    setMpanInput('');
    setPage(1);
  };

  // Render select filter helper
  const renderSelectFilter = (id, label, value, options) => (
    <FormGroup>
      <Label for={id}>{label}</Label>
      <Input type='select' id={id} value={value} onChange={(e) => handleFilterChange(id, e.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Input>
    </FormGroup>
  );

  return (
    <div className='organization'>
      <Row className='subheader'>
        <Col className='col-8'>
          <h1>Defaulted Consumption Data Management</h1>
        </Col>
      </Row>

      <div className='page-cnt'>
        {/* Filters Section */}
        <Row className='mb-4'>
          <Col md={4}>
            <FormGroup>
              <Label for='mpanCore'>MPAN</Label>
              <Input
                type='text'
                id='mpanCore'
                value={mpanInput}
                onChange={handleMpanInputChange}
                placeholder='Enter MPAN to filter...'
              />
            </FormGroup>
          </Col>

          <Col md={4}>
            {renderSelectFilter('connectionType', 'Connection Type', filters.connectionType, CONNECTION_TYPE_OPTIONS)}
          </Col>

          <Col md={4}>
            {renderSelectFilter('settlementCode', 'Settlement Run', filters.settlementCode, SETTLEMENT_CODE_OPTIONS)}
          </Col>
        </Row>

        <Row className='mb-4'>
          <Col md={4} className='d-flex align-items-end'>
            <FormGroup>
              <Button color='primary' onClick={clearAllFilters}>
                Clear All Filters
              </Button>
            </FormGroup>
          </Col>
        </Row>

        {/* Table Section */}
        <Row>
          <Col className='col-12'>
            {loading && <Loading />}
            <BootstrapTable
              classes='table-hover'
              wrapperClasses='table-responsive'
              noDataIndication={() => <div>No records in table.</div>}
              columns={columns}
              data={tableData}
              keyField={'id'}
              bordered={false}
              hover
              remote={{
                filter: false,
                pagination: true,
                sort: true,
              }}
              pagination={paginationFactory({
                page: page,
                sizePerPage: size,
                totalSize: totalSize,
                sizePerPageList: [
                  { text: '5', value: 5 },
                  { text: '10', value: 10 },
                  { text: '20', value: 20 },
                  { text: '50', value: 50 },
                ],
              })}
              onTableChange={handleTableChange}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default DefaultedConsumptionDataManagement;
